/**
 * `ChurnSection` — account-level churn / retention block, sourced from
 * `AdminMetricsOverview.churn` (see `AdminMetricsChurn`).
 *
 * Two headline tiles (churn rate, retention rate), four compact count
 * tiles (churned, reactivated, retained, dormant), and the churn-risk
 * leading-indicator card (at_risk + at_risk_rate, amber-toned).
 *
 * "Real" activity means audit_record rows that are NOT smoke-agent
 * rows AND NOT from an `is_internal` account — the same honest-mode
 * filter the rest of the overview applies. Numbers here agree
 * byte-for-byte with the snapshot tiles.
 *
 * Loading / error states reuse the section-card shape so a partial
 * overview failure doesn't blank the page.
 */

import { AlertTriangle, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import type { AdminMetricsChurn } from '@sill/types';

import type { SetView } from '../../../app/view-router.js';
import { Card } from '../../../components/atoms/Card.js';
import { formatCount, formatPct } from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';
import { SectionHeader } from '../KpiTile.js';
import { ChurnRiskDrillDown } from './ChurnRiskDrillDown.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type ChurnSectionState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; churn: AdminMetricsChurn };

export interface ChurnSectionProps {
  state: ChurnSectionState;
  /** Threaded through so the drill-down can route to an account detail
   *  page via the existing view-router (same path AccountsListView
   *  uses). */
  setView: SetView;
}

const CAPTION =
  'Churn = accounts active 30–60 days ago with no real agent traffic in the last 30 days. ' +
  'Reactivated = comeback after a gap of at least 30 days. Dormant = no real traffic in the last 30 days.';

export function ChurnSection({ state, setView }: ChurnSectionProps): JSX.Element {
  const [drillOpen, setDrillOpen] = useState(false);
  return (
    <section>
      <SectionHeader
        kicker="Retention & churn"
        title="Account activity rollover"
        caption={CAPTION}
      />
      <ChurnBody state={state} onOpenDrill={() => setDrillOpen(true)} />
      <ChurnRiskDrillDown
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
        setView={setView}
      />
    </section>
  );
}

function ChurnBody({
  state,
  onOpenDrill,
}: {
  state: ChurnSectionState;
  onOpenDrill: () => void;
}): JSX.Element {
  const C = useC();

  if (state.kind === 'loading') {
    return (
      <Card style={{ padding: 24 }}>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.dim,
            letterSpacing: 1.5,
          }}
        >
          Loading churn…
        </span>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card style={{ padding: 18 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Churn unavailable
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
          {state.message}
        </div>
      </Card>
    );
  }

  const c = state.churn;

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* Headline pair: churn rate (coral) + retention rate (mint).
          Side-by-side on md+; stacked on small. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RateCard
          kicker="Churn rate"
          rate={c.churn_rate}
          headlineColor={C.coralFg}
          barColor={C.coralFg}
          caption={
            <>
              {formatCount(c.churned)} of {formatCount(c.prior_active_30d)} accounts
              from the prior 30-day window
            </>
          }
        />
        <RateCard
          kicker="Retention rate"
          rate={c.retention_rate}
          headlineColor={C.mintFg}
          barColor={C.mintFg}
          caption={
            <>
              {formatCount(c.retained)} of {formatCount(c.prior_active_30d)} accounts
              stayed active
            </>
          }
        />
      </div>

      {/* Churn-risk leading indicator — amber-toned, distinct from the
          headline rates because it is a watch-bucket, not a result.
          Clickable → opens the per-account drill-down. */}
      <ChurnRiskCard
        atRisk={c.at_risk}
        atRiskRate={c.at_risk_rate}
        activeAccounts30d={c.active_accounts_30d}
        onOpen={onOpenDrill}
      />

      {/* Cohort breakdown — four small count tiles. Coral for churned,
          mint for retained, sky for reactivated (a flow signal, not a
          win/loss), amber for dormant (attention-needed). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CountTile
          kicker="Churned"
          value={formatCount(c.churned)}
          sub="Active 30–60d ago, silent now"
          accent={C.coralFg}
        />
        <CountTile
          kicker="Reactivated"
          value={formatCount(c.reactivated)}
          sub="Returned after a gap of 30d+"
          accent={C.skyFg}
        />
        <CountTile
          kicker="Retained"
          value={formatCount(c.retained)}
          sub="Active in both 30d windows"
          accent={C.mintFg}
        />
        <CountTile
          kicker="Dormant"
          value={formatCount(c.dormant_accounts)}
          sub="No real traffic in last 30d"
          accent={C.amberFg}
        />
      </div>
    </div>
  );
}

interface RateCardProps {
  kicker: string;
  rate: number | null;
  headlineColor: string;
  barColor: string;
  caption: React.ReactNode;
}

function RateCard({
  kicker,
  rate,
  headlineColor,
  barColor,
  caption,
}: RateCardProps): JSX.Element {
  const C = useC();
  const widthPct = rate === null ? 0 : Math.max(0, Math.min(1, rate)) * 100;
  return (
    <Card style={{ padding: 18 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 28,
          color: rate === null ? C.dim : headlineColor,
          fontWeight: 500,
          letterSpacing: -0.6,
          lineHeight: 1.1,
          marginBottom: 12,
        }}
      >
        {formatPct(rate)}
      </div>
      <div
        style={{
          height: 4,
          background: C.surface2,
          border: `1px solid ${C.borderSoft}`,
          borderRadius: 1,
          overflow: 'hidden',
          marginBottom: 8,
        }}
        aria-hidden="true"
      >
        <div
          style={{
            height: '100%',
            width: `${widthPct.toFixed(1)}%`,
            background: barColor,
            transition: 'width 200ms ease-out',
          }}
        />
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {rate === null ? 'No prior 30-day cohort to compare against' : caption}
      </div>
    </Card>
  );
}

interface CountTileProps {
  kicker: string;
  value: string;
  sub: React.ReactNode;
  accent: string;
}

function CountTile({ kicker, value, sub, accent }: CountTileProps): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: accent,
          letterSpacing: 2,
          textTransform: 'uppercase',
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 22,
          color: C.text,
          fontWeight: 500,
          letterSpacing: -0.4,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 0.3,
          lineHeight: 1.4,
        }}
      >
        {sub}
      </div>
    </Card>
  );
}

/* ───────────────────────── churn-risk card ───────────────────────── */

interface ChurnRiskCardProps {
  atRisk: number;
  atRiskRate: number | null;
  activeAccounts30d: number;
  /** Opens the drill-down. When `atRisk === 0` the card renders as
   *  disabled and this is never invoked. */
  onOpen: () => void;
}

/**
 * `ChurnRiskCard` — the `at_risk` + `at_risk_rate` leading indicator.
 *
 * Amber-toned (matches the "attention needed" semantic), with an
 * explicit honest caption: this is a TREND-WATCH bucket, not a
 * prediction model. The count is "accounts active in the 30-day window
 * but silent in the last 14 days" — directly observable, no model.
 *
 * Clickable when `atRisk > 0`: opens a drill-down list of the at-risk
 * accounts for outreach. The whole card is the hit target; activated
 * by click, Enter, or Space. When `atRisk === 0` the card is rendered
 * non-interactive ("No accounts at risk.") — there's nothing to drill
 * into.
 */
function ChurnRiskCard({
  atRisk,
  atRiskRate,
  activeAccounts30d,
  onOpen,
}: ChurnRiskCardProps): JSX.Element {
  const C = useC();
  const [hover, setHover] = useState(false);
  const widthPct =
    atRiskRate === null ? 0 : Math.max(0, Math.min(1, atRiskRate)) * 100;
  const interactive = atRisk > 0;

  // Activation: click + Enter/Space on the role=button. We don't use a
  // <button> at the outer level because the card already contains a
  // bar element with aria-hidden contents; the role=button div keeps
  // the existing layout intact and adds keyboard activation via the
  // onKeyDown shim below.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      style={{
        padding: 18,
        // Subtle amber tint so the card reads as "watch-bucket" without
        // shouting like an alert. A slight lift on hover (interactive
        // path only) advertises the click affordance without a heavy
        // shadow.
        background: interactive && hover ? 'rgba(255,182,39,0.18)' : C.amberDim,
        border: `1px solid rgba(255,182,39,${interactive && hover ? '0.55' : '0.3'})`,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background 120ms ease-out, border-color 120ms ease-out',
        outline: 'none',
      }}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `View ${formatCount(atRisk)} at-risk accounts`,
            onClick: onOpen,
            onKeyDown: handleKeyDown,
            onMouseEnter: () => setHover(true),
            onMouseLeave: () => setHover(false),
            onFocus: () => setHover(true),
            onBlur: () => setHover(false),
          }
        : { 'aria-label': 'No accounts at risk' })}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={13} color={C.amberFg} aria-hidden="true" />
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.amberFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Churn risk — accounts gone quiet (active but no traffic in 14d)
          </div>
        </div>
        {interactive ? (
          <span
            className="inline-flex items-center gap-1 flex-shrink-0"
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.amberFg,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
            aria-hidden="true"
          >
            View list <ChevronRight size={12} />
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 items-end">
        <div className="flex items-baseline" style={{ gap: 8 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 28,
              color: C.text,
              fontWeight: 500,
              letterSpacing: -0.6,
              lineHeight: 1.1,
            }}
          >
            {formatCount(atRisk)}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            accounts
          </div>
        </div>
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
              }}
            >
              At-risk rate
            </span>
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 16,
                color: atRiskRate === null ? C.dim : C.amberFg,
                fontWeight: 500,
                letterSpacing: -0.3,
              }}
            >
              {formatPct(atRiskRate)}
            </span>
          </div>
          <div
            style={{
              height: 4,
              background: C.surface2,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
            aria-hidden="true"
          >
            <div
              style={{
                height: '100%',
                width: `${widthPct.toFixed(1)}%`,
                background: C.amberFg,
                transition: 'width 200ms ease-out',
              }}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: 12,
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {interactive ? (
          <>
            Of {formatCount(activeAccounts30d)} accounts active in the last 30
            days, {formatCount(atRisk)} have recorded no real traffic in the
            last 14 days — trending toward the 30-day dormancy line.{' '}
            <span style={{ fontStyle: 'italic' }}>
              Leading indicator — not a prediction model. Click for the list.
            </span>
          </>
        ) : (
          <>
            No accounts at risk. Every account active in the last 30 days has
            also recorded real traffic in the last 14.
          </>
        )}
      </div>
    </Card>
  );
}
